import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  Visibility as OpenIcon,
  TouchApp as ClickIcon,
  TrendingUp as TrendIcon
} from '@mui/icons-material';
import marketingService from '../services/marketingService';

export default function CampaignAnalytics({ campaignId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, [campaignId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await marketingService.getCampaignAnalytics(campaignId);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No analytics available
      </Typography>
    );
  }

  const StatCard = ({ icon, label, value, color }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: `${color}.lighter`,
              color: `${color}.main`,
              p: 1,
              borderRadius: 1,
              mr: 2
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {analytics.campaign_name}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Sent: {new Date(analytics.sent_at).toLocaleString()}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<EmailIcon />}
            label="Sent"
            value={analytics.sent_count.toLocaleString()}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<OpenIcon />}
            label="Opens"
            value={`${analytics.open_count.toLocaleString()} (${analytics.open_rate}%)`}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<ClickIcon />}
            label="Clicks"
            value={`${analytics.click_count.toLocaleString()} (${analytics.click_rate}%)`}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendIcon />}
            label="Click-to-Open"
            value={`${analytics.click_to_open_rate}%`}
            color="warning"
          />
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Summary
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {analytics.open_rate >= 20 ? (
                <span style={{ color: '#4caf50' }}>
                  ✓ Good open rate (industry average: 15-25%)
                </span>
              ) : (
                <span style={{ color: '#ff9800' }}>
                  ⚠ Consider improving subject line (industry average: 15-25%)
                </span>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {analytics.click_rate >= 2.5 ? (
                <span style={{ color: '#4caf50' }}>
                  ✓ Good click rate (industry average: 2-5%)
                </span>
              ) : (
                <span style={{ color: '#ff9800' }}>
                  ⚠ Consider improving content and CTAs (industry average: 2-5%)
                </span>
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
